import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  getOfferListFromStorage,
  WebshopUtils,
} from '../webshop/webshop-utils';
import {
  ModalMessageType,
  ModalService,
  ModalType,
} from '@proximus/ng-common-components';
import {
  AnalyticsEventData,
  AnalyticsService,
  TranslationService,
} from '@proximus/ng-common';
import { AlarmThresholdModalComponent } from '../../details/option-details/alarm-threshold/alarm-threshold-modal/alarm-threshold-modal.component';
import {
  ApplicationState,
  getAlarmThresholdClosed,
  getAlarmThresholdDeleted,
  getDeepLinkSelectedProduct,
  isPayAndGoOrderSuccess,
} from '../../store/reducers';
import { Store } from '@ngrx/store';
import {
  CloseModalReset,
  DeactivateAlarmThresholdsReset,
} from '../../store/actions/alarm-threshold.actions';
import {
  DeepLinkStatus,
  getMprdProductType,
  isFlexEmergency,
  isNetflixBuyAndSell,
  isOptionActive,
  NetflixBuyAndSellStatus,
  Option,
  OptionStatus,
} from '@dch/products-business-logic';
import { ProductSelectorComponent } from './product-selector/product-selector.component';
import { OnDestroyHookDirective } from '../hooks';
import { OptionLockService } from '../../services/option-lock.service';
import { OptionConfigurationLockService } from '../../services/option-configuration-lock.service';
import {
  buildConfigurationLockKey,
  buildLockKey,
  isIffOption,
  isPending,
  isStpOption,
  retrieveProductAndOptionFromConfirmationEvent,
} from '../order-utils/order-utils';
import { buildTaggingProduct } from '../../tagging/tagging.utils';
import { PayAndGoModalComponent } from '../../details/option-details/pay-and-go-modal/pay-and-go-modal.component';
import { ResetPayAndGo } from '../../store/actions/pay-and-go.actions';
import { BeatsSwapWarningModalComponent } from '../../details/mobile/level-two/priceplan-details/beats-swap-warning-modal/beats-swap-warning-modal.component';
import { OrderFiberBoostConfirmationModalComponent } from '../../details/option-details/order-fiber-boost-confirmation-modal/order-fiber-boost-confirmation-modal.component';
import { Subject } from 'rxjs';

@Component({
  selector: 'mprd-order-button',
  templateUrl: './order-button.component.html',
  styleUrls: ['./order-button.component.css'],
})
export class OrderButtonComponent
  extends OnDestroyHookDirective
  implements OnChanges, OnInit {
  mpcUuid: string;
  installationId;
  accessNumber;
  plc;
  active;
  @Input() selectedProduct;
  suspended;
  @Input() option;
  @Input() isDeepLink: boolean;
  @Input() deepLinkStatus: DeepLinkStatus;
  @Input() orderDisabled: boolean;
  @Input() treatmentCode: string;
  @Input() isSwap = false;
  @Input() isBelfiusPartner = false;
  @Input() packMpcUuid: string;
  @Input() isDataBoost = false;
  @Input() promoCode: string;
  @Input() isDisneyPlusProduct = false;
  @Input() isPremium: boolean;
  @Input() isNimbleRedirection = false;
  @Input() isWebshopLoaded = false;
  @Output() orderDisabledChange = new EventEmitter();
  @Output() optionChange = new EventEmitter<Option>();
  @Output() resultEmit = new EventEmitter();
  @Output() webshopLoadedEvent = new EventEmitter();

  public buttonStateChangeExecutedSubject = new Subject<void>();

  public loading: boolean;
  public isLoadingState: boolean;
  public isPendingInCart: boolean;
  public pendingOrder: boolean;
  public isOrderable;
  public orderLabelKey: string;
  public ceaseLabelKey: string;
  public isWebshopCartInit = false;
  public isAiaPending = false;
  public productType: string;
  public disableButtonClass = '';
  public negativeButtonClass: string;
  // tagging
  public jsonProduct: any;

  DeepLinkStatus = DeepLinkStatus;
  public chargeCode: string;
  public chargeEvent: string;
  private isSubscribed = false;
  private shouldTriggerNimbleAutoOrder = false;
  private hasTriggeredNimbleAutoOrder = false;
  private nimbleRedirectionHasExecuted = false;
  public triggerAddToCartFromSelector = false;
  isSportsFeatureFlagOn: boolean;

  constructor(
    protected modalService: ModalService,
    protected store: Store<ApplicationState>,
    private ngZone: NgZone,
    private analyticsService: AnalyticsService,
    private optionLockService: OptionLockService,
    private optionConfigurationLockService: OptionConfigurationLockService,
    protected translationService: TranslationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.isSportsFeatureFlagOn =
      (this.translationService.getTranslatedValueFor(
        'tv.isSportsFeatureFlag',
      ) ?? 'false') === 'true';
    this.addSubscription(
      this.store.select(getDeepLinkSelectedProduct).subscribe((product) => {
        if (product) {
          this.selectedProduct = product;
          this.option = this.getOption(
            this.selectedProduct.options,
            this.option.optionCodeForDescription || this.option.mpcUuid,
          );
          this.internalOnChanges();
          if (
            !this.isExceedingAlarmThresholdOption() &&
            !this.isPayAndGoOption()
          ) {
            this.triggerAddToCartFromSelector = true;
          }
        } else {
          this.triggerAddToCartFromSelector = false;
        }
        this.calculateLoadingState();
      }),
    );
    this.initNimbleFlow();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      this.option &&
      this.selectedProduct &&
      simpleChanges.hasOwnProperty('option') &&
      JSON.stringify(simpleChanges.option.previousValue) !==
      JSON.stringify(simpleChanges.option.currentValue) &&
      simpleChanges.hasOwnProperty('selectedProduct') &&
      JSON.stringify(simpleChanges.selectedProduct.previousValue) !==
      JSON.stringify(simpleChanges.selectedProduct.currentValue)
    ) {
      this.internalOnChanges();
    }
  }

  private initNimbleFlow() {
    if (
      this.isNimbleRedirection &&
      this.option &&
      this.selectedProduct
    ) {
      this.shouldTriggerNimbleAutoOrder = true;
      if (!this.nimbleRedirectionHasExecuted) {
        this.triggerNimbleAutoOrderAfterDefaultLoad();
      }
    }
  }

  private triggerNimbleAutoOrderAfterDefaultLoad(): void {
    if (
      !this.isNimbleRedirection ||
      !this.shouldTriggerNimbleAutoOrder ||
      this.hasTriggeredNimbleAutoOrder ||
      !this.isWebshopCartInit
    ) {
      return;
    }

    if (!this.validateForNimbleRedirection()) {
      return;
    }

    this.hasTriggeredNimbleAutoOrder = true;
    this.shouldTriggerNimbleAutoOrder = false;

    this.addToCart();
    if (!this.loading) {
      this.proceedWithOrder();
    }
  }

  private validateForNimbleRedirection(): boolean {
    if (!this.selectedProduct || !this.option) {
      this.sendStatusToReactNativeWebView({
        type: 'error',
        action: 'ORDER_CANCELLED',
        message: 'order_product_or_option_not_found',
        msisdn: this.accessNumber
      });
      return false;
    }

    // Check if option is orderable
    if (!this.isOrderable) {
      this.emitNimbleNotOrderableError(false);
      return false;
    }

    // Check if option is suspended
    if (this.suspended) {
      this.emitNimbleSuspendedError();
      return false;
    }

    // Check if there is already a pending order
    if (this.pendingOrder) {
      this.sendStatusToReactNativeWebView({
        type: 'error',
        action: 'ORDER_CANCELLED',
        message: 'option_pending_order',
        msisdn: this.accessNumber
      });
      return false;
    }
    return true;
  }

  private emitNimbleNotOrderableError(shouldSendTaggingEvent: boolean): void {
    if (shouldSendTaggingEvent) {
      this.sendTaggingEvent({
        event: 'button-click',
        button: {
          name: this.getButtonName(),
        },
        action: {
          name: this.getActionName(),
        },
        product: buildTaggingProduct(this.selectedProduct, this.option),
      });
    }

    this.active
      ? this.resultEmit.emit({
          type: 'error',
          action: 'remove',
          ceaseNotAllowed: true,
          mpcUuid: this.mpcUuid,
        })
      : this.resultEmit.emit({
          type: 'error',
          action: 'add',
          cannotOrder: true,
          mpcUuid: this.mpcUuid,
        });

    this.sendStatusToReactNativeWebView({
      type: 'error',
      action: 'ORDER_CANCELLED',
      message: 'option_cease_not_allowed',
      msisdn: this.accessNumber,
    });
  }

  private emitNimbleSuspendedError(): void {
    this.resultEmit.emit({
      type: 'error',
      suspended: true,
      mpcUuid: this.mpcUuid,
    });
    this.sendStatusToReactNativeWebView({
      type: 'error',
      action: 'ORDER_CANCELLED',
      message: 'option_suspended',
      msisdn: this.accessNumber,
    });
  }

  internalOnChanges() {
    if (this.option && this.selectedProduct) {
      this.setProductProperties();
      this.setOrderProperties();
      this.setChargeProperties();
      this.setOrderLabelKey();
      this.ceaseLabelKey = this.getCeaseLabelKey();

      this.handleOptionSpecificLogic();
      this.computeButtonCssClass();
    }
  }

  protected calculateLoadingState(): void {
    this.isLoadingState = this.loading ||
      !this.isWebshopCartInit ||
      this.triggerAddToCartFromSelector ||
      (this.isNimbleRedirection && !this.isWebshopLoaded);

    this.buttonStateChangeExecutedSubject.next();

    console.log('loading', this.loading);
    console.log('isWebshopCartInit', this.isWebshopCartInit);
    console.log('triggerAddToCartFromSelector', this.triggerAddToCartFromSelector);
    console.log('isLoadingState', this.isLoadingState);
    console.log('this.deepLinkStatus', this.deepLinkStatus);
  }

  private handleOptionSpecificLogic() {
    if (this.isNimbleRedirection) {
      this.handleWebshopCartInit();
    } else {
      if (this.isExceedingAlarmThresholdOption()) {
        this.handleExceedingAlarmThresholdOption();
      } else if (this.isPayAndGoOption()) {
        this.handlePayAndGoOption();
      } else {
        this.handleWebshopCartInit();
      }
    }
  }

  private setProductProperties() {
    this.jsonProduct = this.buildJsonProduct();
    this.pendingOrder = this.checkPendingOrder();
    this.suspended = this.checkSuspended();
    this.active = this.checkActiveStatus();
    this.mpcUuid = this.getMpcUuid();
    this.installationId = this.getInstallationId();
    this.accessNumber = this.getAccessNumber();
    this.plc = this.getPlc();
    this.productType = this.getProductType();
  }

  private buildJsonProduct() {
    return buildTaggingProduct(this.selectedProduct, this.option);
  }

  private checkPendingOrder() {
    return isPending(this.selectedProduct, this.option, this.isAiaPending);
  }

  private checkSuspended() {
    return this.option.suspended !== undefined && this.option.suspended;
  }

  private checkActiveStatus() {
    return this.option.ibStatus === 'ACT';
  }

  private getMpcUuid() {
    return this.option.optionCodeForDescription || this.option.mpcUuid;
  }

  private getInstallationId() {
    return this.selectedProduct.installationId;
  }

  private getAccessNumber() {
    return this.selectedProduct.accessNumber;
  }

  private getPlc() {
    return this.selectedProduct.plc;
  }

  private getProductType() {
    return getMprdProductType(this.selectedProduct);
  }

  private setOrderProperties() {
    this.isOrderable = this.active ? this.option.isCeasable === undefined || this.option.isCeasable : this.option.isOrderable === undefined || this.option.isOrderable;
  }

  private setChargeProperties() {
    this.chargeCode = this.getChargeCode();
    this.chargeEvent = this.getChargeEvent();
  }

  protected getChargeCode(): string {
    if (this.isSwap && !this.option.chargeCode) {
      return 'KMLTS';
    }
    return this.option.chargeCode || (this.option.chargeConfiguration && this.option.chargeConfiguration.code);
  }

  protected getChargeEvent(): string {
    if (this.isSwap && !this.option.chargeEvent) {
      return 'RC';
    }
    return this.option.chargeEvent || (this.option.chargeConfiguration && this.option.chargeConfiguration.event);
  }

  private getCeaseLabelKey() {
    return isNetflixBuyAndSell(this.option) && NetflixBuyAndSellStatus.ACTIVE === this.selectedProduct.netflixBuyAndSell ? 'option.details.downgrade' : 'option.details.cease';
  }

  public handleWebshopCartInit() {
    this.isWebshopCartInit = false;
    WebshopUtils.publishCartRetrival(this.installationId, this.accessNumber, this.plc);
    this.subscribeToWebshopEvents();
    this.calculateLoadingState();
  }

  public handleExceedingAlarmThresholdOption() {
    this.store.select(getAlarmThresholdDeleted).subscribe((deleted) => {
      if (deleted !== undefined && deleted) {
        this.resultEmit.emit({ type: 'success', action: 'remove' });
        this.loading = false;
        this.active = false;
        this.option.ibStatus = OptionStatus.AVAILABLE;
        this.optionChange.emit(this.option);
        this.store.dispatch(new DeactivateAlarmThresholdsReset({}));
      } else if (deleted !== undefined && !deleted) {
        this.resultEmit.emit({ type: 'error', action: 'remove' });
        this.loading = false;
        this.store.dispatch(new DeactivateAlarmThresholdsReset({}));
      }
      this.calculateLoadingState();
    });
    this.store.select(getAlarmThresholdClosed).subscribe((value) => {
      if (value !== undefined) {
        this.active = !this.active;
        this.store.dispatch(new CloseModalReset({}));
      }
    });
  }

  public handlePayAndGoOption() {
    this.loading = false;
    this.isWebshopCartInit = true;
    this.calculateLoadingState();

    this.store.select(isPayAndGoOrderSuccess).subscribe((success) => {
      if (success !== undefined) {
        if (success) {
          if (isOptionActive(this.option)) {
            this.resultEmit.emit({ type: 'success', action: 'remove' });
            this.loading = false;
            this.active = false;
            this.option.ibStatus = OptionStatus.AVAILABLE;
            this.optionChange.emit(this.option);
          } else {
            this.resultEmit.emit({ type: 'success', action: 'add' });
            this.sendStatusToReactNativeWebView({ type: 'success', action: 'ORDER_SUCCESFULL', msisdn: this.accessNumber });
            this.loading = false;
            this.active = true;
            this.option.ibStatus = OptionStatus.ACTIVE;
            this.optionChange.emit(this.option);
          }
        } else {
          if (isOptionActive(this.option)) {
            this.resultEmit.emit({ type: 'error', action: 'remove' });
            this.loading = false;
          } else {
            this.resultEmit.emit({ type: 'error', action: 'add' });
            this.sendStatusToReactNativeWebView({ type: 'error', action: 'ORDER_CANCELLED', msisdn: this.accessNumber });
            this.loading = false;
          }
        }
        this.store.dispatch(new ResetPayAndGo());
      }
      this.calculateLoadingState();
    });
  }

  private setOrderLabelKey() {
    if (
      isNetflixBuyAndSell(this.option) &&
      NetflixBuyAndSellStatus.AVAILABLE ===
      this.selectedProduct.netflixBuyAndSell
    ) {
      this.orderLabelKey = 'option.details.upgrade';
    } else if (this.isSwap) {
      this.orderLabelKey = 'mobile.pricingPlan.swap.plan.button';
    } else if (isFlexEmergency(this.option)) {
      this.orderLabelKey = 'flexEmergency.activation.button.label';
    } else if (this.isDisneyPlusProduct) {
      this.orderLabelKey = 'option.details.activate';
    } else {
      this.orderLabelKey = 'option.details.order';
    }
  }

  confirmSwap() {
    WebshopUtils.addProduct(
      false,
      this.mpcUuid,
      'RPO',
      this.chargeEvent,
      this.chargeCode,
      'add',
      getOfferListFromStorage(this.treatmentCode),
      this.promoCode,
    );
  }

  public addToCart() {
    if (this.isNimbleRedirection) {
      this.handleNimbleRedirection();
      return;
    }
    if (this.isSwap && !this.packMpcUuid && this.isBelfiusPartner) {
      this.handleBelfiusSwap();
    } else if (this.isBizzInternetGuarantee()) {
      this.handleBizzInternetGuarantee();
    } else if (
      !this.isExceedingAlarmThresholdOption() &&
      !this.isPayAndGoOption()
    ) {
      this.handleStandardOrder();
    } else {
      this.handleAlarmOrPayAndGo();
    }
  }

  private handleNimbleRedirection(): void {
    if (
      (!isStpOption(this.option, this.isSwap) || this.canOrderOption()) &&
      this.isWebshopCartInit &&
      !this.orderDisabled
    ) {
      if (!this.isOrderable) {
        this.emitNimbleNotOrderableError(true);
      } else if (this.suspended) {
        this.emitNimbleSuspendedError();
      } else {
        this.proceedWithOrder();
      }
    }
  }

  private handleBelfiusSwap(): void {
    this.modalService
      .create<BeatsSwapWarningModalComponent>(
        BeatsSwapWarningModalComponent,
        null,
        ModalMessageType.Warning,
        ModalType.MessageBox,
      )
      .subscribe((componentRef) => {
        componentRef.instance.jsonProduct = this.jsonProduct;
        componentRef.instance.confirm = () => this.confirmSwap();
      });
  }

  private handleBizzInternetGuarantee(): void {
    if (this.active) {
      let taggingErrorText = 'error';
      const errorTagging = this.getBaseErrorTagging(taggingErrorText, {
        message: 'Cease not allowed',
      });
      // cease not allowed message
      this.resultEmit.emit({
        type: 'error',
        action: 'remove',
        ceaseNotAllowed: true,
        mpcUuid: this.mpcUuid,
        errorTagging,
      });
      taggingErrorText = 'CEASE_PRODUCT_NOT_ALLOWED';
    } else {
      // redirection to BIG portal page
      this.sendTaggingEvent({
        event: 'button-click',
        button: {
          name: this.getButtonName(),
        },
        action: {
          name: this.getActionName(),
        },
        product: buildTaggingProduct(this.selectedProduct, this.option),
      });
      window.open(
        this.translationService.getTranslatedValueFor(
          'option.details.big.redirectUrl',
        ),
        '_blank',
      );
    }
  }

  private handleStandardOrder(): void {
    if (
      !(
        (!isStpOption(this.option, this.isSwap) || this.canOrderOption()) &&
        this.isWebshopCartInit &&
        !this.orderDisabled
      )
    ) {
      return;
    }
    if (!this.isOrderable) {
      this.handleNotOrderable();
    } else if (this.suspended) {
      this.emitNimbleSuspendedError();
    } else if (this.isFiberBoostOption()) {
      this.handleFiberBoost();
    } else {
      this.proceedWithOrder();
    }
  }

  private handleNotOrderable(): void {
    this.sendTaggingEvent({
      event: 'button-click',
      button: {
        name: this.getButtonName(),
      },
      action: {
        name: this.getActionName(),
      },
      product: buildTaggingProduct(this.selectedProduct, this.option),
    });
    this.active
      ? this.resultEmit.emit({
        type: 'error',
        action: 'remove',
        ceaseNotAllowed: true,
        mpcUuid: this.mpcUuid,
      })
      : this.resultEmit.emit({
        type: 'error',
        action: 'add',
        cannotOrder: true,
        mpcUuid: this.mpcUuid,
      });
    this.sendStatusToReactNativeWebView({
      type: 'error',
      action: 'ORDER_CANCELLED',
      message: 'option_cease_not_allowed',
      msisdn: this.accessNumber,
    });
  }

  private handleFiberBoost(): void {
    this.modalService
      .create<OrderFiberBoostConfirmationModalComponent>(
        OrderFiberBoostConfirmationModalComponent,
        null,
        ModalMessageType.Warning,
        ModalType.MessageBox,
      )
      .subscribe((componentRef) => {
        componentRef.instance.action = this.active ? 'cease' : 'order';
        componentRef.instance.confirm = () => this.proceedWithOrder();
      });
  }

  private handleAlarmOrPayAndGo(): void {
    if (this.isExceedingAlarmThresholdOption()) {
      this.active = !this.active;
      this.modalService
        .create<AlarmThresholdModalComponent>(AlarmThresholdModalComponent)
        .subscribe((componentRef) => {
          componentRef.instance.option = this.option;
          componentRef.instance.msisdn = this.selectedProduct.accessNumber;
        });
    } else if (this.isPayAndGoOption()) {
      this.modalService
        .create<PayAndGoModalComponent>(PayAndGoModalComponent)
        .subscribe((componentRef) => {
          componentRef.instance.option = this.option;
          componentRef.instance.msisdn = this.selectedProduct.accessNumber;
        });
    }
  }

  private proceedWithOrder() {
    this.loading = true;
    this.resultEmit.emit({ orderingInProgress: true });
    let action: 'remove' | 'add';
    this.calculateLoadingState();

    const isTryAndBuyOptInOrOptOut =
      this.option &&
      this.option.trialOption &&
      this.option.trialOption.endOfTrialDecision !== undefined;

    if (isTryAndBuyOptInOrOptOut) {
      this.executeTrialFlow();
    } else {
      action =
        (this.option.ibStatus === 'AVA' || this.option.ibStatus === undefined
          ? 'add'
          : 'remove');

      this.executeTaggingEventFlow();

      WebshopUtils.addProduct(
        false,
        this.mpcUuid,
        'RPO',
        this.chargeEvent,
        this.chargeCode,
        action,
        getOfferListFromStorage(this.treatmentCode),
        this.promoCode,
        this.isPremium
      );
    }
  }

  protected executeTrialFlow() {
    const endOfTrialDecision =
      this.option.trialOption.endOfTrialDecision === 'CTP' ? 'NCTP' : 'CTP';
    this.sendTaggingEvent({
      event:
        this.option.trialOption.endOfTrialDecision === 'CTP'
          ? 'cancel'
          : 'addtocart',
      eventzone: this.productType + ' options ordering',
      eventdetail:
        this.option.trialOption.endOfTrialDecision === 'CTP'
          ? 'cancel button'
          : 'order button',
      product: buildTaggingProduct(this.selectedProduct, this.option),
    });
    WebshopUtils.configureTryAndBuy(
      endOfTrialDecision,
      this.option.optionCodeForDescription,
      this.option.type,
      getOfferListFromStorage(this.treatmentCode),
    );
  }

  protected executeTaggingEventFlow() {
    if (this.isSwap) {
      this.sendTaggingEvent({
        event: 'swap',
        eventzone: 'alternative plans',
        eventdetail: 'swap button',
        context: this.selectedProduct.groupMpcUuid,
        product: buildTaggingProduct(this.selectedProduct, this.option),
      });
    } else {
      this.sendTaggingEvent({
        event: 'button-click',
        button: {
          name: this.getButtonName(),
        },
        action: {
          name: this.getActionName(),
        },
        product: buildTaggingProduct(this.selectedProduct, this.option),
      });
    }
  }

  protected isExceedingAlarmThresholdOption() {
    return this.mpcUuid === 'EXCEEDING-ALARM';
  }

  private isBizzInternetGuarantee() {
    return this.mpcUuid === 'PWASX';
  }

  private isPayAndGoOption() {
    return this.mpcUuid === 'PG_WORLD';
  }

  public openDeepLinkProductSelectionPopup() {
    this.modalService.create<ProductSelectorComponent>(
      ProductSelectorComponent,
    );
  }

  private getOption(options: Array<Option>, mpcUuid: string) {
    return options.find(
      (value: Option) => value.optionCodeForDescription === mpcUuid,
    );
  }

  public subscribeToWebshopEvents() {
    if (!this.isSubscribed) {
      this.isSubscribed = true;
      WebshopUtils.subscribeToCartEvent(this.handleCartEvent.bind(this), this.addSubscription);
      WebshopUtils.subscribeToConfirmationEvent(this.handleConfirmationEvent.bind(this), this.addSubscription);
      WebshopUtils.subscribeToErrorEvent(this.handleErrorEvent.bind(this), this.addSubscription);
      WebshopUtils.subscribeToCloseEvent(this.handleCloseEvent.bind(this), this.addSubscription);
    }
  }

  private handleCartEvent(data: any) {
    this.ngZone.run(() => {
      let pendingContextTagging = false;
      if (isStpOption(this.option, this.isSwap) && data.status === 'PENDING_ORDER') {
        this.handlePendingOrder();
        pendingContextTagging = true;
      } else if (isIffOption(this.option)) {
        this.handleIffOption();
      } else if (data.status === 'LOADED') {
        this.handleLoadedStatus(data);
      }
      this.isWebshopCartInit = true;
      this.computeButtonCssClass();
      if (this.triggerAddToCartFromSelector) {
        this.triggerAddToCartFromSelector = false;
        this.addToCart();
      }
      if (this.isNimbleRedirection) {
        this.nimbleRedirectionHasExecuted = true;
        this.triggerNimbleAutoOrderAfterDefaultLoad();
      }
      this.webshopLoadedEvent.emit({ pendingContextTagging });
      this.calculateLoadingState();
    });
  }

  private handlePendingOrder() {
    this.isAiaPending = true;
    this.pendingOrder = isPending(this.selectedProduct, this.option, this.isAiaPending);
    this.resultEmit.emit({ type: 'warning', action: 'aiaPending', mpcUuid: this.mpcUuid });
    this.sendStatusToReactNativeWebView({ type: 'warning', action: 'ORDER_PENDING', message: "aiaPending", msisdn: this.accessNumber });
  }

  private handleIffOption() {
    this.isAiaPending = false;
    this.pendingOrder = isPending(this.selectedProduct, this.option, this.isAiaPending);
    if (this.pendingOrder) {
      this.resultEmit.emit({ type: 'warning', action: 'aiaPending', mpcUuid: this.mpcUuid });
      this.sendStatusToReactNativeWebView({ type: 'warning', action: 'ORDER_PENDING', message: "aiaPending", msisdn: this.accessNumber });
    }
  }

  private handleLoadedStatus(data: any) {
    this.isAiaPending = false;
    this.pendingOrder = isPending(this.selectedProduct, this.option, this.isAiaPending);
    if (data.products.length !== 0) {
      const find = data.products.find((p: any) => p.mpcUuid === this.mpcUuid);
      this.isPendingInCart = !!find;
    } else {
      this.isPendingInCart = false;
      this.loading = false;
    }
    if (this.isPendingInCart && !this.loading) {
      this.resultEmit.emit({ type: 'warning', action: 'pending', mpcUuid: this.mpcUuid });
      this.sendStatusToReactNativeWebView({ type: 'warning', action: 'ORDER_PENDING', message: "pending_in_cart", msisdn: this.accessNumber });
      this.orderDisabledChange.emit(true);
    }
    this.calculateLoadingState();
  }

  private handleConfirmationEvent(data: any) {
    this.ngZone.run(() => {
      if (data.productDetails) {
        const { currentProduct, currentOption, isDisneyPlusProduct, isSportsProduct } = retrieveProductAndOptionFromConfirmationEvent(this.selectedProduct, this.mpcUuid, data.productDetails);

        if (( this.isSportsFeatureFlagOn && isSportsProduct) || isDisneyPlusProduct || (currentOption && currentOption.length !== 0) || (this.isSwap && currentProduct && currentProduct.length !== 0 && !currentProduct[0].products)) {
          this.handleConfirmationAction(data);
        }
      }
    });
  }

  private handleConfirmationAction(data: any) {
    if (data.action === 'add') {
      this.handleAddAction(data);
    } else if (data.action === 'remove') {
      this.handleRemoveAction(data);
    } else if (data.action === 'configure') {
      this.handleConfigureAction(data);
    }
    this.loading = false;
    this.calculateLoadingState();
  }

  private handleAddAction(data: any) {
    this.resultEmit.emit({ type: 'success', action: 'add', mpcUuid: this.mpcUuid });
    this.sendStatusToReactNativeWebView({ type: 'success', action: 'ORDER_SUCCESFULL', msisdn: this.accessNumber });
    this.tagOrderSuccess(data);
    if (!this.isSwap) {
      this.lockOption();
    }
  }

  private handleRemoveAction(data: any) {
    this.resultEmit.emit({ type: 'success', action: 'remove', mpcUuid: this.mpcUuid });
    this.lockOption();
  }

  private handleConfigureAction(data: any) {
    this.resultEmit.emit({ type: 'success', action: 'configure', mpcUuid: this.mpcUuid });
    this.configurationLockOption();
  }

  private handleErrorEvent(data: any) {
    this.ngZone.run(() => {
      const errorTagging = this.getBaseErrorTagging('error', data);
      if (data.products) {
        this.handleProductError(data, errorTagging);
      } else {
        this.handleGeneralError(data);
      }
      this.loading = false;
      this.calculateLoadingState();
    });
  }

  private handleProductError(data: any, errorTagging: any) {
    const currentOption = data.products.find((p: any) => p.mpcUuid === this.mpcUuid);
    if (currentOption) {
      this.handleErrorAction(data, errorTagging);
    }
  }

  private handleGeneralError(data: any) {
    if (!data.httpCode || data.httpCode !== 402) {
      this.resultEmit.emit({ type: 'error' });
      this.sendStatusToReactNativeWebView({type: 'error', action: 'ORDER_CANCELLED', message: "general_error", code: data.httpCode, msisdn: this.accessNumber});
    }
    this.loading = false;
    this.isWebshopCartInit = true;
    this.calculateLoadingState();
  }

  private handleErrorAction(data: any, errorTagging: any) {
    if (data.action === 'add') {
      this.handleAddError(data, errorTagging);
    } else if (data.action === 'remove') {
      this.handleRemoveError(data, errorTagging);
    } else if (data.action === 'configure') {
      this.handleConfigureError(data, errorTagging);
    }
  }

  private handleAddError(data: any, errorTagging: any) {
    if (data.errorCode === 5000103) {
      this.emitNoCreditToOrderError();
    } else if (data.httpCode === 402 && data.errorCode === 'shoppingcart.ccw.validation.failed.customer.data') {
      this.emitCwcError(errorTagging);
    } else if (data.httpCode === 400 && data.errorCode === 'CUSTOMER_UNDERAGE') {
      this.emitUnderageError(errorTagging);
    } else {
      this.emitGeneralAddError(errorTagging);
    }
  }

  private emitNoCreditToOrderError() {
    this.resultEmit.emit({ type: 'info', action: 'add', noCreditToOrder: true, mpcUuid: this.mpcUuid });
    this.sendStatusToReactNativeWebView({ type: 'info', action: 'ORDER_NO_CREDIT', message: "no_credit_to_order", msisdn: this.accessNumber });
  }

  private emitCwcError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'add', cwc: true, mpcUuid: this.mpcUuid, errorTagging });
    this.sendStatusToReactNativeWebView({ type: 'error', action: 'ORDER_CWC_ERROR', message: "cwc_error", msisdn: this.accessNumber });
  }

  private emitUnderageError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'add', underage: true, mpcUuid: this.mpcUuid, errorTagging });
    this.sendStatusToReactNativeWebView({ type: 'error', action: 'ORDER_UNDERAGE_ERROR', message: "underage_error", msisdn: this.accessNumber });
  }

  private emitGeneralAddError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'add', mpcUuid: this.mpcUuid, errorTagging });
    this.sendStatusToReactNativeWebView({ type: 'error', action: 'ORDER_CANCELLED', message: "general_error", msisdn: this.accessNumber });
  }

  private handleRemoveError(data: any, errorTagging: any) {
    if (data.message === 'CEASE_PRODUCT_NOT_ALLOWED') {
      this.emitCeaseNotAllowedError(errorTagging);
    } else if (data.message === 'PRODUCT_NOT_CEASABLE') {
      this.emitProductNotCeasableError(errorTagging);
    } else if (data.message === 'CANNOT_CEASE_LAST_MANDATORY_OPTION_OF_GROUP') {
      this.emitLastOptionNotCeasableError(errorTagging);
    } else {
      this.emitGeneralRemoveError(errorTagging);
    }
  }

  private emitCeaseNotAllowedError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'remove', ceaseNotAllowed: true, mpcUuid: this.mpcUuid, errorTagging });
    this.sendStatusToReactNativeWebView({ type: 'error', action: 'ORDER_CANCELLED', message: "cease_not_allowed_error", msisdn: this.accessNumber });
  }

  private emitProductNotCeasableError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'remove', productNotCeasable: true, mpcUuid: this.mpcUuid, errorTagging });
  }

  private emitLastOptionNotCeasableError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'remove', lastOptionNotCeasable: true, mpcUuid: this.mpcUuid, errorTagging });
  }

  private emitGeneralRemoveError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'remove', mpcUuid: this.mpcUuid, errorTagging });
  }

  private handleConfigureError(data: any, errorTagging: any) {
    if (this.isCwcError(data)) {
      this.emitCwcConfigureError(errorTagging);
    } else {
      this.emitGeneralConfigureError(errorTagging);
    }
  }

  private isCwcError(data: any): boolean {
    return data.httpCode === 402 && data.message === 'shoppingcart.ccw.validation.failed.customer.ibl';
  }

  private emitCwcConfigureError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'configure', cwc: true, mpcUuid: this.mpcUuid, errorTagging });
  }

  private emitGeneralConfigureError(errorTagging: any) {
    this.resultEmit.emit({ type: 'error', action: 'configure', mpcUuid: this.mpcUuid, errorTagging });
  }

  private handleCloseEvent(data: any) {
    this.ngZone.run(() => {
      if (data.products) {
        const currentOption = data.products.find((p: any) => p.mpcUuid === this.mpcUuid);
        if (currentOption) {
          this.loading = false;
          this.orderDisabledChange.emit(false);
        }
      }
      this.calculateLoadingState();
    });
  }


  private getBaseErrorTagging(taggingErrorText: string, data: any) {
    const errorTagging = {
      event: 'card-impression',
      card: {
        zone: this.productType + ' options ordering',
        name: taggingErrorText,
      },
      error: {
        type: 'backend',
        message: data.message,
      },
    };
    return errorTagging;
  }

  protected sendTaggingEvent(data: AnalyticsEventData) {
    this.analyticsService.reportEvent(data);
  }

  private sendStatusToReactNativeWebView(details: any): void {
    if (!this.isNimbleRedirection) {
      return;
    }

    const reactNativeWebView = (window as any).ReactNativeWebView;
    if (reactNativeWebView) {
      reactNativeWebView.postMessage(JSON.stringify(details));
    }
  }

  private setLockInSessionStorage() {
    sessionStorage.setItem(
      buildLockKey(this.selectedProduct, this.option),
      Date.now().toString(),
    );
  }

  private lockOption() {
    if (isStpOption(this.option, this.isSwap)) {
      this.optionLockService.lockOption(
        this.option.optionCodeForDescription,
        this.plc,
        this.accessNumber,
      );
      this.setLockInSessionStorage();
      this.pendingOrder = true;
      this.computeButtonCssClass();
    }

    if (isIffOption(this.option)) {
      this.setLockInSessionStorage();
      this.pendingOrder = true;
      this.computeButtonCssClass();
    }
  }

  private configurationLockOption() {
    this.optionConfigurationLockService.configurationLockOption(
      this.option.optionCodeForDescription,
      this.plc,
      this.accessNumber,
    );
    sessionStorage.setItem(
      buildConfigurationLockKey(this.selectedProduct, this.option),
      Date.now().toString(),
    );
    this.pendingOrder = true;
    this.computeButtonCssClass();
  }

  private computeButtonCssClass() {
    if (
      isStpOption(this.option, this.isSwap) ||
      this.isSwap ||
      isIffOption(this.option)
    ) {
      this.disableButtonClass =
        this.orderDisabled || this.isPendingInCart || this.pendingOrder
          ? 'rs-btn-disabled'
          : '';
    }
    this.negativeButtonClass = this.productType === 'tv' ? 'rs-btn-neg' : '';
    this.buttonStateChangeExecutedSubject.next();
  }

  private canOrderOption() {
    return !(this.orderDisabled || this.isPendingInCart || this.pendingOrder);
  }

  private tagOrderSuccess(data: any) {
    if (this.isSwap) {
      this.sendTaggingEvent({
        event: 'popup-impression',
        popup: {
          zone: 'alternative plans',
          name: 'swap success',
        },
        action: {
          name: 'swap',
        },
        product: data.productDetails
          ? JSON.stringify(data.productDetails)
          : buildTaggingProduct(this.selectedProduct, this.option),
        context: this.selectedProduct.activeSubscription.mpcUuid,
      });
    } else {
      this.sendTaggingEvent({
        event: 'popup-impression',
        popup: {
          zone: this.isDataBoost
            ? 'data full speed'
            : this.productType + ' options ordering',
          name: 'order success',
        },
        action: {
          name: 'order',
        },
        product: data.productDetails
          ? JSON.stringify(data.productDetails)
          : buildTaggingProduct(this.selectedProduct, this.option),
      });
    }
  }

  private getButtonName() {
    return this.active ? 'cease' : 'order';
  }

  private getActionName() {
    return this.active ? 'addcease' : 'addtocart';
  }

  private isFiberBoostOption() {
    return this.mpcUuid === 'KUHSI';
  }
}
